import {
  InsuranceCoverage,
  TravelCoverage,
} from "Store/interfaces/products/coverage";
import { getYearlyDiscount, priceMapFrom } from "Utils/priceUtils";
import { TravelCoverageGroup, travelCoverages } from "..";

import { coveragePriceDifference } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { getTravelPriceQueryParams } from "Components/Features/CheckPrice/checkPriceQueryParamUtils";
import { TravelCoverageQueryParam } from "Components/Features/CheckPrice/checkPriceTypes";
import { TravelPriceFormValues } from "./travelProductPriceInterfaces";

export const coveragesFor = (
  insuredOnly: boolean,
  group: TravelCoverageGroup,
  coverages: CoveragePriceCalculation[],
): CoveragePriceCalculation[] =>
  coverages
    .filter(
      ([coverage]: CoveragePriceCalculation) =>
        travelCoverages[coverage].forInsuredOnly === insuredOnly &&
        travelCoverages[coverage].group === group,
    )
    .sort(coveragePriceDifference);

export function getYearlyDiscountValue(
  coverage: string,
  coverages: CoveragePriceCalculation[],
): number | undefined {
  const prices = priceMapFrom(coverages);
  return getYearlyDiscount(prices.get(coverage));
}

export const mapBetweenInsuredAndFamilyCoverage = (
  coverage: InsuranceCoverage,
): InsuranceCoverage => {
  switch (coverage) {
    case TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY:
      return TravelCoverage.TRAVEL_STANDARD;
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY:
      return TravelCoverage.TRAVEL_SUPER;
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_90_DAYS:
      return TravelCoverage.TRAVEL_SUPER_90_DAYS;
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_120_DAYS:
      return TravelCoverage.TRAVEL_SUPER_120_DAYS;
    case TravelCoverage.TRAVEL_STANDARD:
      return TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY;
    case TravelCoverage.TRAVEL_SUPER:
      return TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY;
    case TravelCoverage.TRAVEL_SUPER_90_DAYS:
      return TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_90_DAYS;
    case TravelCoverage.TRAVEL_SUPER_120_DAYS:
      return TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_120_DAYS;
    default:
      return coverage;
  }
};

export const coverageMatchesFamilyOrInsuredOnlyStatus = (
  forInsuredOnly: boolean,
  coverage: InsuranceCoverage,
): boolean => {
  if (forInsuredOnly) {
    switch (coverage) {
      case TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY:
      case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY:
      case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_90_DAYS:
      case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_120_DAYS:
        return true;
      default:
        return false;
    }
  }
  switch (coverage) {
    case TravelCoverage.TRAVEL_STANDARD:
    case TravelCoverage.TRAVEL_SUPER:
    case TravelCoverage.TRAVEL_SUPER_90_DAYS:
    case TravelCoverage.TRAVEL_SUPER_120_DAYS:
      return true;
    default:
      return false;
  }
};

export const getInitialFormStateWithCorrectedCoverage = (
  formState: TravelPriceFormValues,
  forInsuredOnly: boolean,
): TravelPriceFormValues => {
  if (
    coverageMatchesFamilyOrInsuredOnlyStatus(forInsuredOnly, formState.coverage)
  ) {
    return formState;
  }
  return {
    ...formState,
    coverage: mapBetweenInsuredAndFamilyCoverage(formState.coverage),
  };
};

export const getDefaultCoverages = (
  forInsuredOnly: boolean,
): {
  defaultSuperCoverage: TravelCoverage;
  defaultQueryCoverage: TravelCoverage | null;
} => {
  const coverageQueryParam = getTravelPriceQueryParams().coverage;
  const defaultStandardCoverage = forInsuredOnly
    ? TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY
    : TravelCoverage.TRAVEL_STANDARD;
  const defaultSuperCoverage = forInsuredOnly
    ? TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY
    : TravelCoverage.TRAVEL_SUPER;

  if (coverageQueryParam === null) {
    return { defaultSuperCoverage, defaultQueryCoverage: null };
  }

  const defaultQueryCoverage =
    coverageQueryParam === TravelCoverageQueryParam.STANDARD
      ? defaultStandardCoverage
      : defaultSuperCoverage;

  return { defaultSuperCoverage, defaultQueryCoverage };
};

export const getForInsuredOnly = (yourProductState: any): boolean => {
  if (yourProductState?.isInsuranceFor) {
    return yourProductState?.isInsuranceFor === "true";
  }
  return !getTravelPriceQueryParams().familyCoverage;
};
